import type { ProcurementRole, PurchaseRequest } from "./models";
import { procurementStageOf } from "./workflow";

export type ProcurementFilter = {
  id: string;
  label: string;
  matches: (request: PurchaseRequest) => boolean;
};

export type ProcurementWorkspaceTarget = "offers" | "approvals" | "inline";

export function defaultProcurementWorkspaceTarget(
  role: ProcurementRole,
): ProcurementWorkspaceTarget {
  if (role === "SATIN_ALMA") return "offers";
  if (role === "SATIN_ALMA_MUDURU" || role === "GENEL_MUDUR") {
    return "approvals";
  }
  return "inline";
}

export function procurementWorkspaceTarget(
  role: ProcurementRole,
  request: PurchaseRequest,
): ProcurementWorkspaceTarget {
  if (role === "SATIN_ALMA") return "offers";

  if (role === "SATIN_ALMA_MUDURU") {
    const returnedByGeneralManager = request.approvalChain?.some(
      (approval) =>
        approval.role === "GENEL_MUDUR" && approval.status === "REJECTED",
    );
    return request.pendingApprovalRole === "SATIN_ALMA_MUDURU" ||
      returnedByGeneralManager
      ? "approvals"
      : "inline";
  }

  if (role === "GENEL_MUDUR") {
    return request.pendingApprovalRole === "GENEL_MUDUR"
      ? "approvals"
      : "inline";
  }

  return "inline";
}

export function procurementFiltersForRole(
  role: ProcurementRole,
): ProcurementFilter[] {
  if (role === "SATIN_ALMA_MUDURU") {
    return [
      {
        id: "manager-review",
        label: "Onay bekleyenler",
        matches: (request) =>
          request.pendingApprovalRole === "SATIN_ALMA_MUDURU",
      },
      {
        id: "manager-follow-up",
        label: "Takip edilen",
        matches: (request) =>
          request.pendingApprovalRole === "GENEL_MUDUR" ||
          (request.approvalChain?.some(
            (approval) =>
              approval.role === "SATIN_ALMA_MUDURU" &&
              approval.status === "REVISION_REQUESTED",
          ) ??
            false),
      },
      {
        id: "manager-returned",
        label: "Genel Müdürden dönenler",
        matches: (request) =>
          request.approvalChain?.some(
            (approval) =>
              approval.role === "GENEL_MUDUR" && approval.status === "REJECTED",
          ) ?? false,
      },
      {
        id: "manager-completed",
        label: "Tamamlanan",
        matches: (request) => procurementStageOf(request) === "Tamamlandı",
      },
    ];
  }

  if (role === "GENEL_MUDUR") {
    return [
      {
        id: "gm-decision",
        label: "Onay bekleyenler",
        matches: (request) => request.pendingApprovalRole === "GENEL_MUDUR",
      },
      {
        id: "gm-rejected",
        label: "Reddedilen",
        matches: (request) =>
          request.approvalChain?.some(
            (approval) =>
              approval.role === "GENEL_MUDUR" && approval.status === "REJECTED",
          ) ?? false,
      },
      {
        id: "gm-completed",
        label: "Tamamlanan",
        matches: (request) => procurementStageOf(request) === "Tamamlandı",
      },
    ];
  }

  return [
    {
      id: "new-requests",
      label: "Yeni talepler",
      matches: (request) =>
        request.status === "Satın Almaya iletildi" && request.offers === 0,
    },
    {
      id: "offer-collection",
      label: "Teklif toplanıyor",
      matches: (request) =>
        procurementStageOf(request) === "Uzman Hazırlıyor" &&
        !["Düzeltme istendi", "Revizyon"].includes(request.status) &&
        !(request.status === "Satın Almaya iletildi" && request.offers === 0),
    },
    {
      id: "specialist-corrections",
      label: "Güncelleme gerekenler",
      matches: (request) =>
        ["Düzeltme istendi", "Revizyon"].includes(request.status),
    },
    {
      id: "in-approval",
      label: "Onayda",
      matches: (request) =>
        ["Müdür Onayında", "Genel Müdür Onayında"].includes(
          procurementStageOf(request),
        ),
    },
    {
      id: "specialist-completed",
      label: "Tamamlandı",
      matches: (request) => procurementStageOf(request) === "Tamamlandı",
    },
  ];
}

export function defaultProcurementFilterId(
  role: ProcurementRole,
): string | null {
  return procurementFiltersForRole(role)[0]?.id ?? null;
}

export function specialistNextStep(request: PurchaseRequest) {
  if (["Düzeltme istendi", "Revizyon"].includes(request.status)) {
    return {
      title: "Müdür geri bildirimini karşıla",
      description:
        "İstenen ticari alanları güncelleyip dosyayı yeniden gönderin.",
      action: "Dosyayı aç",
      target: "correction" as const,
    };
  }
  if (request.status === "Satın Almaya iletildi" && request.offers === 0) {
    return {
      title: "İlk teklifi kaydet",
      description: "Tedarikçi ve ticari koşulları dosyaya ekleyin.",
      action: "Teklif gir",
      target: "offers" as const,
    };
  }
  if (request.status === "Teklif toplanıyor") {
    return {
      title: "Teklifleri tamamla",
      description: `${request.offers} teklif kayıtlı; karşılaştırmayı tamamlayın.`,
      action: "Teklifleri aç",
      target: "offers" as const,
    };
  }
  if (request.status === "Onaya gönderilmeye hazır") {
    return {
      title: "Dosyayı Müdüre ilet",
      description: "Seçilen teklif ve gerekçe karar için hazır.",
      action: "Kararı gözden geçir",
      target: "offers" as const,
    };
  }
  if (request.pendingApprovalRole) {
    return {
      title: "Onay sürecinde",
      description:
        request.pendingApprovalRole === "SATIN_ALMA_MUDURU"
          ? "Dosya Satın Alma Müdürünün kararını bekliyor."
          : "Dosya Genel Müdürün nihai kararını bekliyor.",
      action: "Süreci incele",
      target: "request" as const,
    };
  }
  return {
    title:
      procurementStageOf(request) === "Tamamlandı"
        ? "Süreç tamamlandı"
        : "Talebi incele",
    description:
      procurementStageOf(request) === "Tamamlandı"
        ? "Nihai karar ve işlem geçmişi kaydedildi."
        : "Talebin güncel durumunu ve geçmişini inceleyin.",
    action: "Talebi aç",
    target: "request" as const,
  };
}
